import mixin from '../privates/utils/mixin';
import Element from './Element';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import quickActionBarUtils from '../privates/utils/quickActionBarUtils';
import StyleProperties from '../privates/styleConstants';
import styleService from './mixins/styleMixins/styleService';

/**
 * @class QuickActionBar
 * @summary A [quick action bar](https://support.wix.com/en/article/adding-and-setting-up-the-quick-action-bar-on-your-mobile-site)
 *  helps your visitors contact you instantly from their mobile devices.
 * @description
 *  You can choose which actions appear on a quick action bar and customize its design to match your site.
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.StyleMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.QuickActionBar
 * @definitionId wysiwyg.viewer.components.QuickActionBar
 * @tagname QuickActionBar
 */

class QuickActionBar extends Element {
    constructor(RMI, compId, contextId, wixSelector, controllerId, role, type) {
        super(RMI, compId, contextId, wixSelector, controllerId, role, type);
        mixin(QuickActionBar.prototype, HiddenCollapsedMixin.prototype);
    }

    /**
     * @member showLabels
     * @label property
     * @syntax
     * get showLabels(): Boolean
     * set showLabels(value: Boolean): void
     * @summary Sets or gets whether a quick action bar's labels are shown.
     * @description
     *  Setting the `showLabels` property to `true` shows labels along with the icons
     *  in the quick action bar. Setting it to `false` hides the labels.
     *
     *  Getting the `showLabels` property returns whether the labels are shown or not.
     * @snippet [QuickActionBar-showLabels_set.es6=Set whether the labels are shown]
     * @snippet [QuickActionBar-showLabels_get.es6=Get whether the labels are shown]
     * @type {external:Boolean}
     * @memberof $w.QuickActionBar
     */
    get showLabels() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return !RMI.getProps(compId).hideText;
    }

  /**
   * @member showLabels
   * @memberof $w.QuickActionBar
   * @param {external:Boolean} showLabels
   */
    set showLabels(showLabels) {
        const compType = members._type.get(this).compType;
        if (!validate({
                compType,
                functionName: 'showLabels',
                dataToValidate: {showLabels},
                compName: this.id
            })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setProps(compId, {hideText: !showLabels});
    }

    /**
     * @member alignment
     * @label property
     * @syntax
     * get alignment(): Alignment
     * set alignment(alignment: Alignment): void
     * type Alignment = "right" | "left"
     * @summary Sets or gets a quick action bar's alignment.
     * @description
     *  Setting the `alignment` property to `"right"` shows the quick action bar on
     *  the right side of the user's device. Setting it to `"left"` shows it on the
     *  left side.
     *
     *  Getting the `alignment` property returns whether the quick action bar is shown
     *  on the left or right side of the user's device.
     * 
     * > **Note:** Only vertical quick action bar designs support the `alignment` property.
     * @snippet [QuickActionBar-alignment_set.es6=Set the quick action bar alignment]
     * @snippet [QuickActionBar-alignment_get.es6=Get the quick action bar alignment]
     * @type {external:Boolean}
     * @memberof $w.QuickActionBar
     */
    get alignment() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getProps(compId).alignment;
    }

  /**
   * @member alignment
   * @memberof $w.QuickActionBar
   * @param {external:Boolean} alignment
   */
    set alignment(alignment) {
        const compType = members._type.get(this).compType;
        if (!validate({
                compType,
                functionName: 'alignment',
                dataToValidate: {alignment},
                compName: this.id
            })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setProps(compId, {alignment});
    }

    /**
     * @member colorScheme
     * @label property
     * @syntax
     * get colorScheme(): ColorScheme
     * set colorScheme(colorScheme: ColorScheme): void
     * type ColorScheme = "grey" | "black" | "brand"
     * @summary Sets or gets a quick action bar's color scheme.
     * @description
     *  One of:
     *
     *  + `"grey"`: Buttons are grey.
     *  + `"black"`: Buttons are black.
     *  + `"brand"`: Buttons are various colors.
     * @snippet [QuickActionBar-colorScheme_set.es6=Set the color scheme]
     * @snippet [QuickActionBar-colorScheme_get.es6=Get the color scheme]
     * @type {external:String}
     * @memberof $w.QuickActionBar
     */
    get colorScheme() {
        const colorSchemeStyleProperty = styleService.getStyleParamValue.call(this, StyleProperties.COLOR_SCHEME, false);
        return quickActionBarUtils.getColorSchemeParts(colorSchemeStyleProperty).colorScheme;
    }

  /**
   * @member colorScheme
   * @memberof $w.QuickActionBar
   * @param {external:String} colorScheme
   */
    set colorScheme(colorScheme) {
        const compName = members._compName.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
                compType,
                functionName: 'colorScheme',
                dataToValidate: {colorScheme},
                compName
            })) {
            return this;
        }

        const currentColorScheme = styleService.getStyleParamValue.call(this, StyleProperties.COLOR_SCHEME, false);
        const currentInvertColorsPart = quickActionBarUtils.getColorSchemeParts(currentColorScheme).invertColorScheme;
        const newColorSchemeStyleProperty = colorScheme.concat(currentInvertColorsPart);

        styleService.setStyle.call(this, StyleProperties.COLOR_SCHEME, newColorSchemeStyleProperty, false);
    }

    /**
     * @member invertColorScheme
     * @label property
     * @syntax
     * get invertColorScheme(): Boolean
     * set invertColorScheme(value: Boolean): void
     * @summary Sets or gets whether a quick action bar's color scheme is inverted.
     * @description
     *  Setting the `invertColorScheme` property to `true` applies the quick action
     *  bar's colors to the bar's background. Setting it to `false` applies the colors
     *  to the bar's icons.
     *
     *  Getting the `invertColorScheme` property returns whether the color scheme is
     *  inverted or not.
     * @snippet [QuickActionBar-invertColorScheme_set.es6=Set whether the color scheme is inverted]
     * @snippet [QuickActionBar-invertColorScheme_get.es6=Get whether the color scheme is inverted]
     * @type {external:Boolean}
     * @memberof $w.QuickActionBar
     */
    get invertColorScheme() {
        const colorSchemeStyleProperty = styleService.getStyleParamValue.call(this, StyleProperties.COLOR_SCHEME, false);
        return !!quickActionBarUtils.getColorSchemeParts(colorSchemeStyleProperty).invertColorScheme;
    }

  /**
   * @member invertColorScheme
   * @memberof $w.QuickActionBar
   * @param {external:Boolean} invertColorScheme
   */
    set invertColorScheme(invertColorScheme) {
        const compName = members._compName.get(this);
        const compType = members._type.get(this).compType;

        if (!validate({
                compType,
                functionName: 'invertColorScheme',
                dataToValidate: {invertColorScheme},
                compName
            })) {
            return this;
        }

        const currentColorScheme = styleService.getStyleParamValue.call(this, StyleProperties.COLOR_SCHEME, false);
        const currentColorSchemeParts = quickActionBarUtils.getColorSchemeParts(currentColorScheme);
        const shouldUpdateInvertColors = !!currentColorSchemeParts.invertColorScheme !== invertColorScheme;
        if (shouldUpdateInvertColors) {
            const newColorSchemeStyleProperty = quickActionBarUtils.getColorSchemeStyleProperty(currentColorSchemeParts.colorScheme, invertColorScheme);
            styleService.setStyle.call(this, StyleProperties.COLOR_SCHEME, newColorSchemeStyleProperty, false);
        }
    }

    /**
     * @function onItemClicked
     * @syntax
     * function onItemClicked(handler: QuickActionBarItemClickedEventHandler): QuickActionBar
     * callback QuickActionBarItemClickedEventHandler(event: QuickActionBarItemClickedEvent, $w: Function): void
     * @summary Adds an event handler that runs when an item in a quick action bar is
     *  clicked.
     *
     * > **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  [here](https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers).
     * @param {$w.QuickActionBarItemClickedEventHandler} handler The name of the
     *  function or the function expression to run when a quick action bar item is
     *  clicked.
     * @returns {$w.QuickActionBar} The quick action bar that triggered the event.
     * @snippet [QuickActionBar-onItemClicked.es6=Get the information of the item that was clicked]
     * @memberof $w.QuickActionBar
     * @eventtype itemClicked
     * @eventhandler
     */
    onItemClicked(cb) {
        const compType = members._type.get(this).compType;

        if (validate({
                compType,
                functionName: 'onItemClicked',
                dataToValidate: {onItemClicked: cb},
                compName: this.id
            })) {

            const RMI = members._RMI.get(this);
            const contextId = members._contextId.get(this);
            const controllerId = members._controllerId.get(this);
            const compId = members._compId.get(this);
            RMI.registerEvent(contextId, compId, RMI.getEventTypes().QUICK_ACTION_BAR_ITEM_CLICKED, controllerId, cb);
        }
        return this;
    }

}

export default QuickActionBar;
